import React from 'react';
import { Phone, PhoneOff, User, Ticket, Star, Loader2 } from 'lucide-react';
import { formatPhoneNumber, cn } from '../../utils/helpers';
import { CallerLookupResponse } from '../../services/support';

interface IncomingCallModalProps {
  callerId?: string;
  callerName?: string;
  callerInfo?: CallerLookupResponse | null;
  callerLookupLoading?: boolean;
  onAccept: () => void;
  onReject: () => void;
  onSelectTicket?: (ticketId: string) => void;
}

const IncomingCallModal: React.FC<IncomingCallModalProps> = ({
  callerId,
  callerName,
  callerInfo,
  callerLookupLoading,
  onAccept,
  onReject,
  onSelectTicket,
}) => {
  const hasCallerInfo = callerInfo?.found && callerInfo.user;
  const hasOpenTickets = callerInfo?.openTickets && callerInfo.openTickets.length > 0;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" />

      {/* Modal */}
      <div className={cn(
        "relative bg-card border border-border rounded-2xl p-8 shadow-2xl animate-slide-up mx-4",
        hasOpenTickets ? "max-w-md w-full" : "max-w-sm w-full"
      )}>
        {/* Animated rings */}
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <div className="w-32 h-32 rounded-full border-2 border-primary/20 animate-ping" />
          <div
            className="absolute w-40 h-40 rounded-full border-2 border-primary/10 animate-ping"
            style={{ animationDelay: '0.2s' }}
          />
        </div>

        {/* Content */}
        <div className="relative flex flex-col items-center">
          {/* Avatar */}
          <div className="w-20 h-20 rounded-full bg-primary/20 flex items-center justify-center mb-4 ring-animation overflow-hidden">
            {callerInfo?.user?.profileImage ? (
              <img 
                src={callerInfo.user.profileImage} 
                alt={callerInfo.user.name || 'Caller'} 
                className="w-full h-full object-cover"
              />
            ) : (
              <User className="h-10 w-10 text-primary" />
            )}
          </div>

          {/* Caller info */}
          <h3 className="text-xl font-bold text-foreground mb-1">
            Incoming Call
          </h3>
          
          {callerLookupLoading ? (
            <div className="flex items-center gap-2 text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" />
              <span className="text-sm">Looking up caller...</span>
            </div>
          ) : (
            <>
              {(callerName || callerInfo?.user?.name) && (
                <p className="text-lg font-medium text-foreground">
                  {callerName || callerInfo?.user?.name}
                </p>
              )}
              {callerId && (
                <p className="text-sm font-mono text-muted-foreground">
                  {formatPhoneNumber(callerId)}
                </p>
              )}
              
              {/* Additional caller info */}
              {hasCallerInfo && callerInfo.user && (
                <div className="flex items-center gap-3 mt-2 text-xs text-muted-foreground">
                  <span className="px-2 py-0.5 bg-primary/10 text-primary rounded-full">
                    {callerInfo.user.userType}
                  </span>
                  {callerInfo.user.customerRating && (
                    <span className="flex items-center gap-1">
                      <Star className="h-3 w-3 text-yellow-500" />
                      {callerInfo.user.customerRating.toFixed(1)}
                    </span>
                  )}
                </div>
              )}
            </>
          )}

          {/* Open Tickets */}
          {hasOpenTickets && (
            <div className="w-full mt-4 pt-4 border-t border-border">
              <p className="text-xs font-semibold text-muted-foreground mb-2 flex items-center gap-1">
                <Ticket className="h-3 w-3" />
                Open Tickets ({callerInfo.openTickets.length})
              </p>
              <div className="space-y-1 max-h-24 overflow-y-auto">
                {callerInfo.openTickets.slice(0, 3).map((ticket) => (
                  <button
                    key={ticket.id}
                    onClick={() => onSelectTicket?.(ticket.id)}
                    className="w-full text-left p-2 rounded-lg bg-background/50 hover:bg-background transition-colors"
                  >
                    <p className="text-sm font-medium text-foreground truncate">
                      {ticket.title}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {ticket.status} • {ticket.priority}
                    </p>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Unknown caller notice */}
          {!callerLookupLoading && !hasCallerInfo && callerId && (
            <p className="text-xs text-muted-foreground mt-2 text-center">
              Unknown caller - not in system
            </p>
          )}

          {/* Call buttons */}
          <div className="flex items-center gap-6 mt-6">
            <button
              onClick={onReject}
              className="w-16 h-16 rounded-full bg-destructive flex items-center justify-center text-white hover:bg-destructive/90 transition-colors shadow-lg shadow-destructive/30"
            >
              <PhoneOff className="h-7 w-7" />
            </button>
            <button
              onClick={onAccept}
              className="w-16 h-16 rounded-full bg-success flex items-center justify-center text-white hover:bg-success/90 transition-colors shadow-lg shadow-success/30 call-pulse"
            >
              <Phone className="h-7 w-7" />
            </button>
          </div>

          {/* Labels */}
          <div className="flex items-center gap-12 mt-3">
            <span className="text-xs text-destructive font-medium">Decline</span>
            <span className="text-xs text-success font-medium">Accept</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default IncomingCallModal;
